/**
 * Pure batch-suggestion ranking algorithm — no DB/Express dependency.
 *
 * Used by the parent-facing Add Kid batch picker (client/src/pages/parent/AddKid.tsx)
 * and, later, the coach-facing "Suggest batch" action on unassigned players
 * (feat/suggest-batch-ranking, not yet started). Both import from here so the
 * ranking logic and DB→algorithm mapping conventions stay identical between
 * the two entry points.
 *
 * Callers are responsible for mapping DB shapes onto the plain inputs below
 * BEFORE calling suggestBatches — this file does no normalization itself:
 *   - players.skillLevel / batches.skillLevel are capitalized ("Beginner")
 *     in the DB — lowercase before passing in as `level`.
 *   - players.ballType ("Tennis ball" | "Hard/Leather ball") and
 *     batches.ballType ("tennis" | "hard") use DIFFERENT vocabularies by
 *     design (see shared/schema.ts comment on batches.ballType) — normalize
 *     both to the same lowercase form ("tennis" | "hard") before calling.
 *   - preferredDays / days are JSON text in SQLite — parse before calling
 *     (server/routes/batches.ts's toPublicBatch already does this for `days`).
 */

export interface SuggestBatchesPlayerInput {
  ballType: string;              // normalized, e.g. "tennis" | "hard"
  level: string;                 // normalized, e.g. "beginner"
  dob: string;                   // "YYYY-MM-DD"
  preferredDays: string[];       // e.g. ["Mon", "Wed"]
  preferredTimeSlot?: string | null;
  // Optional — a scoring term, not a hard filter. AddKid.tsx no longer
  // collects this from the parent; when absent, the day-overlap threshold
  // bonus and the sessions-per-week match bonus both degrade to their
  // fallback (partial day-overlap credit, no bonus) rather than throwing.
  sessionsPerWeek?: number;
}

export interface SuggestBatchesBatchInput {
  id: number;
  name: string;
  ballType: string;              // normalized, e.g. "tennis" | "hard"
  level: string;                 // normalized, e.g. "beginner"
  ageBand: string;                // e.g. "5-8", "U11", "9+"
  days: string[];
  timeSlot?: string | null;
  sessionsPerWeek?: number | null;
  capacity: number;
  currentCount: number;
}

export interface RankedBatch {
  id: number;
  name: string;
  days: string[];
  timeSlot: string | null;
  capacity: number;
  currentCount: number;
  score: number;
  isBestFit: boolean;
}

export interface SuggestBatchesResult {
  suggestions: RankedBatch[];
  reason?: string;
}

const LEVELS = ["beginner", "intermediate", "advanced"];

/**
 * Parses an age-band string into an inclusive [min, max] age range.
 * Returns null for anything unrecognized — callers must exclude that batch
 * defensively rather than let a malformed band silently mis-rank it.
 *
 *   "5-8"  -> [5, 8]     plain range
 *   "U11"  -> [0, 10]    U{N} = age <= N-1
 *   "9+"   -> [9, 99]    open-ended
 */
export function parseAgeBand(band: string): [number, number] | null {
  if (!band) return null;
  const trimmed = band.trim();

  // Real production value, not a hypothetical — several live batches
  // (Hard Ball Beginners, a handful of "All ages" Tennis batches) use this
  // literal string. Same open-ended sentinel convention as "N+" below.
  if (/^all ages$/i.test(trimmed)) {
    return [0, 99];
  }

  const openEnded = trimmed.match(/^(\d+)\+$/);
  if (openEnded) {
    return [Number(openEnded[1]), 99];
  }

  // Combined-bracket naming, e.g. "U13/15" — two adjacent under-brackets
  // merged into one shared training group (real batch: "Hard Ball U13/15").
  // The younger bracket is always a subset of the older one (U13 ⊂ U15),
  // so the combined band is just the wider bracket's own range. Matches
  // this file's own test fixture (H4, "Hard Ball U13/U15 B"), which already
  // modeled a combined U13/U15 batch as ageBand: 'U15' before this case
  // existed — this just accepts the real raw string instead of requiring
  // callers to pre-simplify it.
  const uCompound = trimmed.match(/^U(\d+)\/(\d+)$/i);
  if (uCompound) {
    const n = Math.max(Number(uCompound[1]), Number(uCompound[2]));
    return [0, n - 1];
  }

  const uFormat = trimmed.match(/^U(\d+)$/i);
  if (uFormat) {
    const n = Number(uFormat[1]);
    return [0, n - 1];
  }

  const plainRange = trimmed.match(/^(\d+)-(\d+)$/);
  if (plainRange) {
    const min = Number(plainRange[1]);
    const max = Number(plainRange[2]);
    if (min > max) return null;
    return [min, max];
  }

  return null;
}

export function calculateAgeYears(dob: string): number {
  const dobMs = new Date(dob).getTime();
  const nowMs = Date.now();
  const msPerYear = 365.25 * 24 * 60 * 60 * 1000;
  return Math.floor((nowMs - dobMs) / msPerYear);
}

export function suggestBatches(
  player: SuggestBatchesPlayerInput,
  batches: SuggestBatchesBatchInput[],
): SuggestBatchesResult {
  const ageYears = calculateAgeYears(player.dob);

  const eligible = batches.filter((batch) => {
    if (batch.ballType !== player.ballType) return false;
    if (batch.currentCount >= batch.capacity) return false;

    const band = parseAgeBand(batch.ageBand);
    if (!band) return false;
    const [min, max] = band;
    return ageYears >= min && ageYears <= max;
  });

  if (eligible.length === 0) {
    return {
      suggestions: [],
      reason: "No matching batch with a free spot — create a new batch or assign manually",
    };
  }

  const scored: RankedBatch[] = eligible.map((batch) => {
    let score = 0;

    const diff = Math.abs(LEVELS.indexOf(batch.level) - LEVELS.indexOf(player.level));
    score += diff === 0 ? 40 : diff === 1 ? 20 : 0;

    const overlap = player.preferredDays.filter((d) => batch.days.includes(d)).length;
    // player.sessionsPerWeek is optional — when absent, always fall through
    // to partial day-overlap credit rather than the >=sessionsPerWeek bonus.
    score += (player.sessionsPerWeek != null && overlap >= player.sessionsPerWeek) ? 30 : overlap * 10;

    if (player.preferredTimeSlot && batch.timeSlot === player.preferredTimeSlot) {
      score += 15;
    }

    if (batch.sessionsPerWeek != null && batch.sessionsPerWeek === player.sessionsPerWeek) {
      score += 10;
    }

    score += batch.capacity - batch.currentCount;

    return {
      id: batch.id,
      name: batch.name,
      days: batch.days,
      timeSlot: batch.timeSlot ?? null,
      capacity: batch.capacity,
      currentCount: batch.currentCount,
      score,
      isBestFit: false,
    };
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (b.capacity - b.currentCount) - (a.capacity - a.currentCount);
  });

  const top3 = scored.slice(0, 3);
  if (top3.length > 0) {
    top3[0].isBestFit = true;
  }

  return { suggestions: top3 };
}
